import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  ModalCloseButton,
  useDisclosure,
  ModalFooter,
  Skeleton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  VStack,
} from '@chakra-ui/react';
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useGetSummarizedLoansQuery } from '../../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { SearchBarInput } from 'packages/common-frontend/components/SearchAndCopyComponents';
import { truncateUuid } from 'packages/common-frontend/utils';
import { SummarizedLoanFragmentGQLType } from 'packages/loans/requests/loans.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { tableHeaderStyle } from '~/packages/common-frontend/styles';

type LoanOption = {
  value: string;
  loan: SummarizedLoanFragmentGQLType;
};

type SearchSelectModalProps = {
  upsizeModalControls: ReturnType<typeof useDisclosure>;
};

const TableCell = (cellValue: string) => {
  return (
    <Td overflow="hidden" whiteSpace="normal">
      {cellValue}
    </Td>
  );
};

const TableRow = ({
  loanOption,
  onSelect,
}: {
  loanOption: LoanOption;
  onSelect: (option: LoanOption) => void;
}) => {
  const facilityValue = `${loanOption.loan.creditFacilityName}${
    loanOption.loan.externalFacilityId
      ? ` (${loanOption.loan.externalFacilityId})`
      : ''
  }`;
  const loanValue = `${truncateUuid(loanOption.loan.id)}${
    loanOption.loan.externalLoanId ? ` (${loanOption.loan.externalLoanId})` : ''
  }`;
  return (
    <Tr
      _hover={{ backgroundColor: colors.gray[50] }}
      cursor="pointer"
      onClick={() => onSelect(loanOption)}
    >
      {TableCell(facilityValue)}
      {TableCell(loanValue)}
      {TableCell(loanOption.loan.name || '-')}
    </Tr>
  );
};

const TableHeaderCell = (label: string) => {
  return (
    <Th
      sx={{
        ...tableHeaderStyle,
        padding: '',
        position: 'sticky',
        top: 0,
      }}
    >
      {label}
    </Th>
  );
};

const UpsizeLoansTable = ({
  options,
  onSelectLoanOption,
}: {
  options: LoanOption[];
  onSelectLoanOption: (option: LoanOption) => void;
}) => {
  return (
    <TableContainer maxH="22rem" minW="100%" overflowY="scroll">
      <Table>
        <Thead>
          <Tr>
            {TableHeaderCell('Facility')}
            {TableHeaderCell('Loan ID')}
            {TableHeaderCell('Loan Name')}
          </Tr>
        </Thead>
        <Tbody>
          {options.map((option) => (
            <TableRow
              key={option.value}
              loanOption={option}
              onSelect={onSelectLoanOption}
            />
          ))}
          {options.length === 0 && (
            <Tr>
              <Td colSpan={3} textAlign="center">
                No results found.
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export const UpsizeSearchSelectModal = ({
  upsizeModalControls,
}: SearchSelectModalProps) => {
  const { facilities } = useCreditFacilitiesOrThrow();
  const [searchQuery, setSearchQuery] = useState('');
  const [loanOption, setLoanOption] = useState<LoanOption | null>(null);

  const onClose = () => {
    setLoanOption(null);
    setSearchQuery('');
    upsizeModalControls.onClose();
  };

  // @jendevelops TODO: need to update this query on any loan creating mutation, so we don't have to refetch this every time
  const { data, loading: isLoading, error } = useGetSummarizedLoansQuery({
    variables: {
      filterParams: {
        creditFacilityIds: facilities.map((facility) => facility.id),
        calculatedLoanStatuses: ['initiated', 'funded', 'overdue'],
      },
    },
  });

  const options = useMemo(() => {
    if (data?.loans) {
      return data.loans.map((loan) => ({
        value: loan.id,
        loan,
      }));
    }
    return [];
  }, [data]);

  if (error) {
    throw error;
  }

  const onSelectLoanOption = (option: LoanOption) => {
    setLoanOption(option);
    setSearchQuery(option.loan.id);
  };

  const filteredOptions = useMemo(() => {
    if (!searchQuery) {
      return options;
    }

    const search = searchQuery.toLowerCase();
    const isMatch = (input?: string | null): boolean => {
      if (!input) return false;
      return input.toLowerCase().includes(search);
    };

    return options.filter((option) => {
      return (
        isMatch(option.loan.creditFacilityName) ||
        isMatch(option.loan?.externalFacilityId) ||
        isMatch(option.loan.id) ||
        isMatch(option.loan?.externalLoanId) ||
        isMatch(option.loan.name)
      );
    });
  }, [options, searchQuery]);

  return (
    <Modal {...upsizeModalControls} onClose={onClose}>
      <ModalOverlay />
      <ModalContent minWidth="50vw">
        <ModalHeader pt="2rem" pb="0.5rem">
          <Typography variant="section-title">Upsize Existing Loan</Typography>
          <Typography fontWeight="medium" trim>
            Search by Facility Name, External Facility ID, Loan ID, or External
            Loan ID
          </Typography>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody mb="0.5rem">
          <>
            {(() => {
              if (isLoading || !data?.loans) {
                return <Skeleton height="1rem" width="100%" />;
              }

              return (
                <VStack gap="1rem" width="100%">
                  <SearchBarInput
                    query={searchQuery}
                    onChangeQuery={setSearchQuery}
                  />
                  <UpsizeLoansTable
                    options={filteredOptions}
                    onSelectLoanOption={onSelectLoanOption}
                  />
                </VStack>
              );
            })()}
          </>
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={!loanOption}
            colorScheme="v2-aurora"
            variant="primary"
            as={Link}
            to={{
              pathname: '/funding-requests/upsize',
              search: `?creditFacilityId=${loanOption?.loan.creditFacilityId}&loanId=${loanOption?.loan.id}`,
            }}
          >
            Next
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
